import {VerificationChallengeTaskDto} from "../api/model/verification-dto";
import {ChallengeNonceParams} from "../api/model/verification-challenge-submit-params";
import {powWorkerScript} from "./pow-worker-script";

interface PendingTask {
  taskId: number;
  task: VerificationChallengeTaskDto;
  difficulty: number;
  resolve: (result: ChallengeNonceParams) => void;
  reject: (error: Error) => void;
}

interface ResultMessage {
  type: 'result';
  taskId: number;
  result: ChallengeNonceParams;
}

export class WorkerPool {
  private readonly objectUrl: string;
  private readonly maxWorkers: number;
  private readonly workers: Worker[] = [];
  private readonly idleWorkers: Worker[] = [];
  private readonly inFlight = new Map<Worker, PendingTask>();
  private readonly queue: PendingTask[] = [];
  private nextTaskId = 0;
  private isReset = false;

  constructor() {
    const blob = new Blob([powWorkerScript], { type: "application/javascript" });
    this.objectUrl = URL.createObjectURL(blob);
    this.maxWorkers = Math.min(navigator.hardwareConcurrency || 4, 8);
  }

  public enqueue(task: VerificationChallengeTaskDto, difficulty: number): Promise<ChallengeNonceParams> {
    return new Promise((resolve, reject) => {
      if (this.isReset) {
        reject(new Error("WorkerPool has been reset"));
        return;
      }
      this.queue.push({ taskId: this.nextTaskId++, task, difficulty, resolve, reject });
      this.dispatch();
    });
  }

  private dispatch(): void {
    while (!this.isReset && this.queue.length > 0) {
      let worker = this.idleWorkers.pop();
      if (!worker && this.workers.length < this.maxWorkers) {
        worker = this.spawnWorker();
      }
      if (!worker) return;
      const pending = this.queue.shift()!;
      this.inFlight.set(worker, pending);
      worker.postMessage({
        type: 'task',
        taskId: pending.taskId,
        task: pending.task,
        difficulty: pending.difficulty,
      });
    }
  }

  private spawnWorker(): Worker {
    const worker = new Worker(this.objectUrl);

    worker.onmessage = (event: MessageEvent<ResultMessage>) => {
      if (this.isReset) return;
      const message = event.data;
      const pending = this.inFlight.get(worker);
      if (!pending || !message || message.type !== 'result' || message.taskId !== pending.taskId) {
        return;
      }
      this.inFlight.delete(worker);
      this.idleWorkers.push(worker);
      pending.resolve(message.result);
      this.dispatch();
    };

    worker.onerror = () => {
      const pending = this.inFlight.get(worker);
      this.inFlight.delete(worker);
      this.removeWorker(worker);
      worker.terminate();
      if (pending && !this.isReset) {
        pending.reject(new Error("Worker error"));
      }
      this.dispatch();
    };

    this.workers.push(worker);
    return worker;
  }

  private removeWorker(worker: Worker): void {
    const wIdx = this.workers.indexOf(worker);
    if (wIdx !== -1) this.workers.splice(wIdx, 1);
    const iIdx = this.idleWorkers.indexOf(worker);
    if (iIdx !== -1) this.idleWorkers.splice(iIdx, 1);
  }

  public reset(): void {
    this.isReset = true;
    for (const worker of this.workers) {
      worker.terminate();
    }
    this.workers.length = 0;
    this.idleWorkers.length = 0;
    this.inFlight.clear();
    for (const pending of this.queue) {
      pending.reject(new Error("WorkerPool has been reset"));
    }
    this.queue.length = 0;
    URL.revokeObjectURL(this.objectUrl);
  }
}
